/**
 * Settings page - Desktop full-page implementation
 *
 * Replaces the previous modal approach with a proper full-page route.
 * Sidebar category navigation + form content area, with browser back support.
 */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { SettingsCategory } from "../types/settings";
import { SETTINGS_CATEGORIES } from "../constants/settings";
import { FORM_COMPONENTS, CATEGORY_LABELS } from "../constants/formRegistry";
import { SettingsLegalFooter } from "../components/SettingsLegalFooter";
import { cn } from "../utils/cn";

interface LocationState {
  category?: SettingsCategory;
  returnTo?: string;
}

function SettingsWeb() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? {}) as LocationState;

  const initialCategory =
    state.category && SETTINGS_CATEGORIES.includes(state.category)
      ? state.category
      : "profile";

  const [activeCategory, setActiveCategory] =
    useState<SettingsCategory>(initialCategory);

  // Track the location key we last synced from to avoid overriding sidebar clicks
  const [lastSyncedLocationKey, setLastSyncedLocationKey] = useState(
    location.key
  );

  // Sync active category only when navigated with a NEW location state
  if (
    state.category &&
    SETTINGS_CATEGORIES.includes(state.category) &&
    location.key !== lastSyncedLocationKey
  ) {
    setLastSyncedLocationKey(location.key);
    setActiveCategory(state.category);
  }

  const handleBack = () => {
    navigate(state.returnTo ?? "/dashboard");
  };

  const FormComponent = FORM_COMPONENTS[activeCategory];

  return (
    <div className="settings-page">
      {/* Header with back button */}
      <div className="settings-fullpage-header">
        <button
          onClick={handleBack}
          className="settings-fullpage-back"
          aria-label="Back"
        >
          ←
        </button>
        <h1 className="settings-fullpage-title">Settings</h1>
      </div>

      {/* Settings body: sidebar + content */}
      <div className="settings-modal-body">
        {/* Side Navigation */}
        <div className="settings-nav">
          <div className="settings-nav-menu">
            {SETTINGS_CATEGORIES.map((categoryId) => (
              <button
                key={categoryId}
                onClick={() => setActiveCategory(categoryId)}
                className={cn(
                  "settings-nav-item",
                  activeCategory === categoryId && "settings-nav-item-active"
                )}
              >
                {CATEGORY_LABELS[categoryId]}
              </button>
            ))}
          </div>
        </div>

        {/* Settings content */}
        <div className="settings-content">
          <FormComponent key={activeCategory} />
        </div>
      </div>

      {/* Auto-save indicator */}
      <div
        className="settings-autosave-indicator"
        id="settings-autosave-indicator"
      >
        <svg
          className="settings-autosave-icon"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm3.78-9.72a.75.75 0 0 0-1.06-1.06L7.25 8.69 5.28 6.72a.75.75 0 0 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4-4z" />
        </svg>
        <span>Changes are saved automatically</span>
      </div>

      {/* Legal Footer */}
      <SettingsLegalFooter variant="desktop" />
    </div>
  );
}

export default SettingsWeb;
